<template>
  <div class="card p-4">
    <div class="row">
      <!-- logo -->
      <div class="col-1 text-center">
        <img
          :alt="platform"
          class="avatar-img avatar-sm rounded-circle"
          :src="
            platform === 'ovation'
              ? '/theme/img/platforms/ovation-logo.svg'
              : `/theme/img/platforms/${platform}.svg`
          "
        />
      </div>

      <!-- name, stars, location -->
      <div class="col">
        <div>
          <span class="mr-2">{{ authorName }} </span>
          <StarRating :rating="rating" v-if="isReview && rating" />
          <img
            :src="`/img/ratings/${populatedFeedback.rating}.png`"
            :alt="`rating ${populatedFeedback.rating} emoji`"
            contain
            height="18"
            class="mt--2"
            v-else-if="!isReview && populatedFeedback.rating"
          />
        </div>
        <div class="text-muted mt-1">
          <span>
            <i class="fas fa-map-marker-alt"></i>
            {{ location }}
          </span>
          <span class="ml-3" v-if="isSurvey && populatedFeedback.source">
            <i class="fas fa-tags fa-sm"></i>
            {{ populatedFeedback.source }}
          </span>
        </div>
      </div>

      <!-- datetime stamp -->
      <div class="col-3 text-muted text-right mr-2">
        <i class="fas fa-clock"></i>
        {{ timestamp }}
      </div>
    </div>

    <div class="row">
      <div class="col-1"></div>

      <div class="col">
        <!-- feedback -->
        <div class="my-3">
          <text-highlight :queries="highlightQuery" :highlightStyle="highlightStyles">
            {{ feedback }}
          </text-highlight>
        </div>

        <!-- badges -->
        <span v-for="(badge, index) in badgeList" :key="index" class="h2 mr-2">
          <div
            class="badge mb-1"
            :class="badgeColor(badge.sentiment)"
            :style="{ color: badge.sentiment === 'POSITIVE' ? 'green' : 'red' }"
          >
            {{ formatCategoryWithParent(badge._id) }}
          </div>
        </span>
      </div>

      <div class="col-1"></div>
    </div>
  </div>
</template>

<script>
import StarRating from '@/components/Modules/Review/StarRating'
import { createNamespacedHelpers } from 'vuex'

const InsightsModule = createNamespacedHelpers('insightsv2')
const CompetitionDetailsModule = createNamespacedHelpers('competitionDetails')

export default {
  name: 'CompetitionDetailsListCard',
  props: {
    populatedFeedback: {
      type: Object,
      required: true
    }
  },
  components: {
    StarRating
  },
  computed: {
    ...InsightsModule.mapGetters([
      'selectCategory',
      'selectCategoryName',
      'selectCategoryParentMap',
      'selectNonOtherCategories'
    ]),
    ...CompetitionDetailsModule.mapGetters(['selectFilters']),
    badgeList() {
      return this.populatedFeedback.classifications.map((c) => ({
        _id: c.categoryId,
        category: c.categoryName,
        sentiment: c.sentiment
      }))
    },
    platform() {
      return this.populatedFeedback?.platform || 'ovation-logo'
    },
    isSurvey() {
      return this.platform === 'ovation'
    },
    isReview() {
      return !this.isSurvey
    },
    authorName() {
      return this.populatedFeedback?.customerName || 'Anonymous'
    },
    rating() {
      return this.populatedFeedback?.rating
    },
    location() {
      return this.populatedFeedback?.locationLabel
    },
    timestamp() {
      return this.$moment(this.populatedFeedback.timestamp).format('LL')
    },
    highlightQuery() {
      if (this.selectShowHighlight) {
        const classificationToHighlight = this.populatedFeedback.classifications.find(
          (c) => c.categoryId === this.selectShowHighlight
        )
        return classificationToHighlight?.text || ''
      }
      return ''
    },
    selectShowHighlight() {
      // For competition, we can highlight based on selected category
      const filters = this.selectFilters
      if (filters?.category && filters?.sentiment) {
        return filters.category
      }
      return false
    },
    highlightStyles() {
      const sentiment = this.selectFilters?.sentiment
      if (!sentiment) return
      return {
        padding: '0px',
        'background-color': 'white',
        'border-bottom': `2px solid ${sentiment === 'POSITIVE' ? '#008000' : '#ff0000'}`,
        'border-radius': '0px'
      }
    },
    feedback() {
      return this.populatedFeedback.feedback
    }
  },
  methods: {
    badgeColor(sentiment) {
      return sentiment === 'POSITIVE' ? { 'bg-light-green': true } : { 'bg-light-red': true }
    },
    formatCategoryWithParent(categoryId) {
      const category = this.selectNonOtherCategories.find((c) => c._id === categoryId)
      if (!category) return

      if (!category.path) {
        return category.name
      }

      const parentId = category.path.split('/').pop()
      const parentCategory = this.selectCategory(parentId)

      if (!parentCategory) return category.name

      return `${parentCategory.name}: ${category.name}`
    }
  }
}
</script>

<style scoped>
.bg-light-green {
  background: #ccf7e5;
}
.bg-light-red {
  background: #ffcccb;
}
</style>
